import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { StockService } from '../../stock/stock.service';
import { PaymentService } from '../payment.service';
import { GetTransferRunsRequest, PaymentStatus, TransferRun } from '../../stock/stock.type';
import { AuthService } from '../../auth/auth.service';
import { Viewport } from '../../shared/viewport';
import { SupplierInfoDialog } from '../../supplier/supplier-info-dialog/supplier-info-dialog';
import { EditFeeDialog } from '../../stock/edit-fee-dialog/edit-fee-dialog';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog';
import { PaymentState } from '../payment.state';
import { paymentLabel, statusClass } from '../payment-format';

/** The Payments "Transfer runs" tab: finished stock runs and what each is owed. */
@Component({
  selector: 'app-payment-runs',
  imports: [
    CommonModule, FormsModule, MatTableModule, MatButtonModule, MatIconModule, MatFormFieldModule,
    MatSelectModule, MatCheckboxModule, MatProgressSpinnerModule, MatTooltipModule, MatPaginatorModule,
  ],
  templateUrl: './payment-runs.html',
  styleUrl: './payment-runs.scss',
})
export class PaymentRuns implements OnInit {
  readonly state = inject(PaymentState);
  private stockService = inject(StockService);
  private paymentService = inject(PaymentService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  readonly isMobile$ = inject(Viewport).isMobile$;

  readonly paymentClass = statusClass;
  readonly paymentLabel = paymentLabel;
  readonly pageSizeOptions = [25, 50, 100, 200];
  /** The most the API returns per page — used when fetching every unpaid run. */
  private readonly maxPageSize = 200;

  readonly columns = ['select', 'date', 'supplier', 'account', 'coins', 'fee', 'payable', 'payment'];

  runs: TransferRun[] = [];
  total = 0;
  pageIndex = 0;
  pageSize = 50;
  loading = false;
  error: string | null = null;

  /** Unpaid by default — this is the list an admin works through to pay. */
  paymentStatus = 'NotPayed';
  /** Runs that transferred 0 coins are hidden by default, even for admins. */
  includeZeroCoinRuns = false;

  /** Set while every unpaid run of the supplier is being fetched for select-all. */
  selectingAll = false;
  /** Number of runs picked by "select all unpaid", or null while the selection is manual. */
  allUnpaidCount: number | null = null;

  get canEdit(): boolean {
    return this.authService.canWrite('suppliers_payments');
  }

  ngOnInit(): void {
    this.load();
    this.state.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => { this.pageIndex = 0; this.allUnpaidCount = null; this.load(); });
  }

  private baseRequest(): GetTransferRunsRequest {
    return {
      ...this.state.filters(),
      includeZeroCoinRuns: this.includeZeroCoinRuns,
      // Runs still transferring are not payable until they finish.
      payableOnly: true,
    };
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.stockService.getTransferRuns({
      ...this.baseRequest(),
      paymentStatus: this.paymentStatus || undefined,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    }).subscribe({
      next: res => {
        this.runs = res.runs;
        this.total = res.total;
        this.loading = false;
      },
      error: err => { this.error = err?.error?.error ?? 'Failed to load transfer runs'; this.loading = false; },
    });
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.load();
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.load();
  }

  // ── Selection ──

  /** Only unpaid runs are selectable — settling a paid run would be a no-op. */
  get selectable(): TransferRun[] {
    return this.runs.filter(r => r.payment_status !== 'Payed');
  }

  get selectedOnPage(): number {
    return this.selectable.filter(r => this.state.runs.has(r.id)).length;
  }

  get pageAllSelected(): boolean {
    return this.selectable.length > 0 && this.selectedOnPage === this.selectable.length;
  }

  get pageSomeSelected(): boolean {
    return this.selectedOnPage > 0 && !this.pageAllSelected;
  }

  /** Select-all needs one supplier in view — a settlement can't span several. */
  get canSelectAll(): boolean {
    return !!this.state.supplierId && this.selectable.length > 0;
  }

  /** More unpaid runs may exist beyond this page. */
  get canSelectAllPages(): boolean {
    return this.pageAllSelected && this.allUnpaidCount === null
      && (this.paymentStatus !== 'NotPayed' || this.total > this.selectable.length);
  }

  private select(r: TransferRun): void {
    this.state.runs.set(r.id, { supplierId: r.supplier_id, amount: r.payable_amount });
  }

  toggle(r: TransferRun): void {
    this.allUnpaidCount = null;
    if (this.state.runs.has(r.id)) this.state.runs.delete(r.id);
    else this.select(r);
  }

  togglePage(): void {
    this.allUnpaidCount = null;
    if (this.pageAllSelected) {
      for (const r of this.selectable) this.state.runs.delete(r.id);
    } else {
      for (const r of this.selectable) this.select(r);
    }
  }

  /** Selects every unpaid run of the filtered supplier, across all pages. */
  selectAllUnpaid(): void {
    this.selectingAll = true;
    this.fetchAllUnpaid().subscribe({
      next: all => {
        for (const r of all) this.select(r);
        this.allUnpaidCount = all.length;
        this.selectingAll = false;
      },
      error: err => {
        this.selectingAll = false;
        this.snackBar.open(err?.error?.error ?? 'Failed to select all unpaid runs', 'Close', { duration: 6000 });
      },
    });
  }

  private fetchAllUnpaid(): Observable<TransferRun[]> {
    const request = { ...this.baseRequest(), paymentStatus: 'NotPayed', pageSize: this.maxPageSize };
    return this.stockService.getTransferRuns({ ...request, pageIndex: 0 }).pipe(
      switchMap(first => {
        if (first.totalPages <= 1) return of(first.runs);
        const rest = Array.from({ length: first.totalPages - 1 }, (_, i) =>
          this.stockService.getTransferRuns({ ...request, pageIndex: i + 1 }));
        return forkJoin(rest).pipe(map(pages => [first.runs, ...pages.map(p => p.runs)].flat()));
      }),
    );
  }

  clearSelection(): void {
    this.allUnpaidCount = null;
    this.state.clearSelection();
  }

  // ── Row actions ──

  openSupplierInfoDialog(supplierId: string, supplierName?: string): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(SupplierInfoDialog, {
      data: { supplierId: supplierId, supplierUsername: supplierName },
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    });
  }

  openEditFeeDialog(run: TransferRun): void {
    const ref = this.dialog.open(EditFeeDialog, {
      width: '360px',
      maxWidth: '95vw',
      data: {
        runLabel: run.supplier_name || run.id.slice(0, 8),
        feeSnapshot: run.fee_snapshot,
      },
    });
    ref.afterClosed().subscribe((feeSnapshot: number | null) => {
      if (feeSnapshot === null || feeSnapshot === undefined) return;
      this.paymentService.updateTransferRunFee(run.id, feeSnapshot).subscribe({
        next: () => {
          this.snackBar.open('Fee snapshot updated', 'Close', { duration: 4000 });
          // Its payable changed, so a selection made at the old amount is void.
          this.state.runs.delete(run.id);
          this.allUnpaidCount = null;
          this.load();
        },
        error: err => this.snackBar.open(err?.error?.error ?? 'Failed to update fee', 'Close', { duration: 6000 }),
      });
    });
  }

  /**
   * Direct admin override, independent of the settlement flow — for
   * correcting a mismarked run or writing off one paid outside it. Reverting
   * a Payed run detaches it from whatever settlement it belonged to.
   */
  togglePaymentStatus(run: TransferRun): void {
    const next: PaymentStatus = run.payment_status === 'Payed' ? 'NotPayed' : 'Payed';
    const label = run.supplier_name || run.id.slice(0, 8);
    const message = next === 'Payed'
      ? `Mark this run for ${label} as paid? This bypasses the settlement flow.`
      : `Mark this run for ${label} as unpaid? If it was part of a settlement, it will be detached from it.`;
    this.dialog.open(ConfirmDialog, {
      data: { title: 'Change payment status', message, confirmText: next === 'Payed' ? 'Mark paid' : 'Mark unpaid' },
    }).afterClosed().subscribe(ok => {
      if (!ok) return;
      this.paymentService.updateTransferRunPaymentStatus(run.id, next).subscribe({
        next: () => {
          this.snackBar.open('Payment status updated', 'Close', { duration: 4000 });
          this.state.runs.delete(run.id);
          this.allUnpaidCount = null;
          this.load();
        },
        error: err => this.snackBar.open(err?.error?.error ?? 'Failed to update payment status', 'Close', { duration: 6000 }),
      });
    });
  }
}
